import axios from 'utils/axios';
import { createSlice } from '@reduxjs/toolkit';
import { snakeCase } from 'change-case';

const convertEvent2Enum = (searchFilter) => {
  if (['eventSubType', 'eventType'].includes(searchFilter.column)) {
    // `in` carries a comma-separated list — snake-case each entry, not the whole string, or the
    // commas get folded away and the list becomes one bogus value.
    if (searchFilter.operator === 'in') {
      const value = String(searchFilter.value || '')
        .split(',')
        .map((v) => snakeCase(v.trim()))
        .filter(Boolean)
        .join(',');
      return { ...searchFilter, value };
    }
    return { ...searchFilter, value: snakeCase(searchFilter.value) };
  }
  return searchFilter;
};

const initialState = {
  list: {
    isLoading: true,
    error: false,
    data: [],
    count: 0,
    /** Latest list fetch target appId (Number); ignore stale responses). */
    requestAppId: null,
  },
  detail: {
    isLoading: true,
    error: false,
    data: {},
    history: [],
  },
  associateUserWithTransaction: {
    isLoading: false,
    error: false,
    data: {},
  },
};

const slice = createSlice({
  name: 'revenueManagementTransactions',
  initialState,
  reducers: {
    transactionsStartLoading(state, action) {
      state.list.isLoading = true;
      state.list.data = [];
      state.list.requestAppId = Number(action.payload);
    },

    transactionsHasError(state, action) {
      state.list.isLoading = false;
      state.list.error = action.payload;
    },

    transactionsSuccess(state, action) {
      const { appId, transactions, count } = action.payload;
      if (Number(appId) !== state.list.requestAppId) {
        return;
      }
      state.list.isLoading = false;
      state.list.data = transactions;
      state.list.count = count;
    },

    transactionByIdStartLoading(state) {
      state.detail.isLoading = true;
      state.detail.data = [];
    },

    transactionByIdHasError(state, action) {
      state.detail.isLoading = false;
      state.detail.error = action.payload;
    },

    transactionByIdSuccess(state, action) {
      const { history, transaction } = action.payload;

      // Handle both RMUser (can be array or single object) and RMUsers array
      let processedTransaction = transaction;
      let usersArray = null;

      // Check if RMUser is an array (backend sometimes sends array)
      if (transaction.RMUser && Array.isArray(transaction.RMUser) && transaction.RMUser.length > 0) {
        usersArray = transaction.RMUser;
      } else if (transaction.RMUsers && Array.isArray(transaction.RMUsers) && transaction.RMUsers.length > 0) {
        usersArray = transaction.RMUsers;
      } else if (transaction.RMUser && !Array.isArray(transaction.RMUser)) {
        // Single user object
        usersArray = [transaction.RMUser];
      }

      if (usersArray && usersArray.length > 0) {
        // Sort by creationDate descending (most recent first)
        const sortedUsers = [...usersArray].sort((a, b) => {
          const dateA = a.creationDate ? new Date(a.creationDate).getTime() : 0;
          const dateB = b.creationDate ? new Date(b.creationDate).getTime() : 0;
          return dateB - dateA; // Descending order (newest first)
        });
        // Select the most recent user (first in sorted array) and keep the sorted array
        processedTransaction = {
          ...transaction,
          RMUser: sortedUsers[0] || null,
          RMUsers: sortedUsers, // Keep sorted array for UI selection
        };
      }

      state.detail.isLoading = false;
      state.detail.data = processedTransaction;
      state.detail.history = history;
    },

    associateUserWithTransactionSuccess(state, action) {
      const response = action.payload;
      // Handle response structure: could be { transaction, users } or direct transaction object
      const transactionData = response.transaction || response;
      const usersData = response.users || response.RMUser || response.RMUsers || null;

      let processedData = transactionData;

      // Handle both RMUser (can be array or single object), RMUsers array, and users array
      let usersArray = null;

      // Check different possible formats
      if (usersData && Array.isArray(usersData) && usersData.length > 0) {
        usersArray = usersData;
      } else if (transactionData.RMUser && Array.isArray(transactionData.RMUser) && transactionData.RMUser.length > 0) {
        usersArray = transactionData.RMUser;
      } else if (
        transactionData.RMUsers &&
        Array.isArray(transactionData.RMUsers) &&
        transactionData.RMUsers.length > 0
      ) {
        usersArray = transactionData.RMUsers;
      } else if (transactionData.RMUser && !Array.isArray(transactionData.RMUser)) {
        // Single user object
        usersArray = [transactionData.RMUser];
      } else if (usersData && !Array.isArray(usersData)) {
        // Single user object in users field
        usersArray = [usersData];
      }

      if (usersArray && usersArray.length > 0) {
        // Sort by creationDate descending (most recent first)
        const sortedUsers = [...usersArray].sort((a, b) => {
          const dateA = a.creationDate ? new Date(a.creationDate).getTime() : 0;
          const dateB = b.creationDate ? new Date(b.creationDate).getTime() : 0;
          return dateB - dateA; // Descending order (newest first)
        });
        // Select the most recent user (first in sorted array) and keep the sorted array
        processedData = {
          ...transactionData,
          RMUser: sortedUsers[0] || null,
          RMUsers: sortedUsers, // Keep sorted array for UI selection
        };
      }

      state.associateUserWithTransaction.isLoading = false;
      state.associateUserWithTransaction.data = processedData;
    },

    associateUserWithTransactionHasError(state, action) {
      state.associateUserWithTransaction.isLoading = false;
      state.associateUserWithTransaction.error = action.payload;
    },

    associateUserWithTransactionStartLoading(state) {
      state.associateUserWithTransaction.isLoading = true;
    },
  },
});

export default slice.reducer;

export function getRevenueManagementTransactions(appId, page, pageSize, search = []) {
  return async (dispatch) => {
    const id = Number(appId);
    const searchQuery = search.length > 0 ? `&search=${JSON.stringify(search.map(convertEvent2Enum))}` : '';
    dispatch(slice.actions.transactionsStartLoading(id));
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/transactions?order=eventDate${searchQuery}&ordering=desc&page=${page}&limit=${pageSize}`,
      );

      dispatch(slice.actions.transactionsSuccess({ ...response, appId: id }));
    } catch (error) {
      dispatch(slice.actions.transactionsHasError(error));
    }
  };
}

export function getRevenueManagementTransactionById(appId, transactionId) {
  return async (dispatch) => {
    dispatch(slice.actions.transactionByIdStartLoading());
    try {
      const response = await axios.get(`/revenue-management/${appId}/transactions/${transactionId}`);
      dispatch(slice.actions.transactionByIdSuccess(response));
    } catch (error) {
      dispatch(slice.actions.transactionByIdHasError(error));
    }
  };
}

export function getAssociatedTransactionAndUser(appId, transactionId, originalTransactionId) {
  return async (dispatch) => {
    dispatch(slice.actions.associateUserWithTransactionStartLoading());
    try {
      const response = await axios.get(`/revenue-management/${appId}/transactions/${transactionId}/users`);

      dispatch(slice.actions.associateUserWithTransactionSuccess(response));
      // Return the processed data for component use
      return { success: true, data: response };
    } catch (error) {
      dispatch(slice.actions.associateUserWithTransactionHasError(error));
      return { success: false, error: error.message || 'Failed to fetch associated users' };
    }
  };
}
